import { IDENTITY_PATHS, brokeredLogin } from '@hds/identity-client-sdk'

/**
 * The paths a signed-out person may visit. Everything else redirects to the
 * hosted login. Kept tight; a new public page is added here explicitly.
 * `/account-not-linked` is public-by-token: the person holds a provider-verified
 * token that resolves to no live Human.
 */
export const PUBLIC_PATHS = Object.freeze(new Set([IDENTITY_PATHS.login, IDENTITY_PATHS.accountNotLinked]))

/**
 * The router's `beforeEach`, mirroring the portal's.
 *
 * `outcome` is what `placeBootOutcome` returned for this page load, and it is
 * read at navigation time rather than copied here: a load that already
 * completed (or failed) a hosted-login round trip must NOT bounce back to the
 * identity provider — that is an infinite loop. It lands on the choice screen
 * (unlinked / restorable) or the retry screen (failed) with the reason instead.
 *
 * Starting the hosted login is awaited: it can refuse before it navigates
 * (no client id configured, no crypto), and a refusal that went unheard would
 * leave the page blank with nothing on screen. It lands on the retry card
 * with the reason instead, where the person can see what is wrong.
 *
 * Registered BEFORE `app.use(router)`: `use(router)` triggers the initial
 * navigation immediately, and a guard added after that never runs for it.
 */
export function createAuthGuard({ session, home = '/', outcome = {} }) {
    return async function guard(to) {
        const isPublic = PUBLIC_PATHS.has(to.path)
        if (!session.isAuthenticated && !isPublic) {
            if (outcome.unlinkedLanding) {
                return { ...outcome.unlinkedLanding, replace: true }
            }
            if (outcome.loginRoundTripFailed) {
                return { path: IDENTITY_PATHS.login, query: { error: outcome.loginRoundTripFailed }, replace: true }
            }
            // On-load login: no in-app login screen — straight to the hosted page.
            try {
                await brokeredLogin.beginLogin(to.fullPath)
                return false
            } catch (error) {
                return { path: IDENTITY_PATHS.login, query: { error: error.message }, replace: true }
            }
        }
        // A signed-in person has no business on the login screen.
        if (session.isAuthenticated && to.path === IDENTITY_PATHS.login) {
            return { path: home, replace: true }
        }
        return true
    }
}
